import { useCallback, useEffect, useState } from 'react'
import { AmplitudeRenderer } from '@renderer/utils/amplitude.renderer'
import { usePlanRevampAnnounced, usePricingEdition } from '@renderer/chat/hooks/use-plan-features'
import { useUnifiedBilling } from '@renderer/chat/store/global-billing'
import { PlanBanner } from './plan-banner'
import { useDismissedPlanBanners, useDismissPlanBanner } from './plan-banner-state'
import {
  planChangeAnnouncementCopy,
  resolvePlanChangeAnnouncement,
  type PlanChangeAnnouncement,
} from './plan-change-rules'

const SURFACE = 'sidepanel home'

/** The help centre's article on the new plans. The main process hands the URL to the browser. */
const PLAN_REVAMP_HELP_URL = "https://help.highlightai.com/articles/9716351004-What's-changing-with-Highlight's-plans"

/** The banner's 150ms fade, with room to finish before it unmounts. */
const DISMISS_FADE_MS = 300

/**
 * Tells a 2.0 subscriber what the pricing revamp does to their plan on the
 * flip day, once its flag starts it. Its action opens the help centre, since
 * the in-app plan comparison still shows the old lineup before the flip. The
 * dismissal holds until the date or the tier changes.
 */
export function SidepanelPlanChangeBanner() {
  const { subscription } = useUnifiedBilling()
  const edition = usePricingEdition()
  const announced = usePlanRevampAnnounced()
  const dismissed = useDismissedPlanBanners()
  const dismiss = useDismissPlanBanner()

  const announcement = resolvePlanChangeAnnouncement({ plan: subscription.plan, edition, announced })
  const dismissKey = announcement?.dismissKey
  const onDismiss = useCallback(() => {
    if (dismissKey) dismiss(dismissKey)
  }, [dismiss, dismissKey])

  if (!announcement || !dismissKey || dismissed.includes(dismissKey)) return null
  return <ShownPlanChangeBanner key={dismissKey} announcement={announcement} onDismiss={onDismiss} />
}

function ShownPlanChangeBanner({
  announcement,
  onDismiss,
}: {
  announcement: PlanChangeAnnouncement
  onDismiss: () => void
}) {
  const { tier, ctaPlan } = announcement
  const [leaving, setLeaving] = useState(false)

  useEffect(() => {
    AmplitudeRenderer.trackEvent('Paywall', {
      action: 'shown',
      feature: 'plan-change',
      reason: tier,
      plan: ctaPlan,
      from: SURFACE,
    })
  }, [tier, ctaPlan])

  const onAction = useCallback(() => {
    AmplitudeRenderer.trackEvent('Paywall', {
      action: 'clicked',
      feature: 'plan-change',
      reason: tier,
      plan: ctaPlan,
      from: SURFACE,
    })
    window.open(PLAN_REVAMP_HELP_URL, '_blank')
  }, [tier, ctaPlan])

  // The X fades the banner out before the dismissal unmounts it. Home
  // unmounts under a drill-in view, so a dismissal mid-fade lands on cleanup.
  useEffect(() => {
    if (!leaving) return
    let settled = false
    const settle = () => {
      if (settled) return
      settled = true
      onDismiss()
    }
    const timer = window.setTimeout(settle, DISMISS_FADE_MS)
    return () => {
      window.clearTimeout(timer)
      settle()
    }
  }, [leaving, onDismiss])

  return (
    <PlanBanner
      kind="plan-change"
      copy={planChangeAnnouncementCopy(announcement)}
      ctaPlan={ctaPlan}
      onAction={onAction}
      onDismiss={() => setLeaving(true)}
      className={leaving ? 'opacity-0' : undefined}
    />
  )
}
